import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import PasswordInput from '../components/PasswordInput';

function redirectAfterAuth(navigate, target = '/') {
  try { navigate(target, { replace: true }); } catch (e) {}
  setTimeout(() => {
    if (['/login', '/register'].includes(window.location.pathname)) {
      window.location.replace(target);
    }
  }, 400);
}

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, googleAuth, linkGoogle, forgotPasswordStart, forgotPasswordVerify, isAuthenticated, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [mode, setMode] = useState('login');
  const [forgotEmail, setForgotEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [linkRequired, setLinkRequired] = useState(false);
  const [linkEmail, setLinkEmail] = useState('');
  const [linkPassword, setLinkPassword] = useState('');
  const [pendingGoogleToken, setPendingGoogleToken] = useState('');

  const returnTo = searchParams.get('returnTo') || '/';
  const needsGoogleLink = searchParams.get('linkGoogle') === '1';

  // Auto-redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !needsGoogleLink && !linkRequired) {
      redirectAfterAuth(navigate, returnTo);
    }
  }, [isAuthenticated, needsGoogleLink, linkRequired, navigate, returnTo]);

  useEffect(() => {
    if (needsGoogleLink && user) {
      toast.info('Now click "Sign in with Google" to link your Google account.');
      sessionStorage.setItem('pendingGoogleLink', 'true');
    }
  }, [needsGoogleLink, user]);

  const handleGoogleSuccess = useCallback(async (credential) => {
    setGoogleLoading(true);
    try {
      const pending = sessionStorage.getItem('pendingGoogleLink');
      if (pending === 'true' && user) {
        await linkGoogle(credential);
        sessionStorage.removeItem('pendingGoogleLink');
        toast.success('Google account linked!');
        redirectAfterAuth(navigate, '/');
        return;
      }

      const result = await googleAuth(credential, 'signin');

      if (result.status === 'LINK_REQUIRED') {
        setGoogleLoading(false);
        setLinkRequired(true);
        setLinkEmail(result.email || '');
        setPendingGoogleToken(credential);
        return;
      }

      if (result.status === 'LOGGED_IN' || result.status === 'CREATED') {
        toast.success(result.status === 'CREATED' ? 'Account created!' : 'Welcome back!');
        redirectAfterAuth(navigate, returnTo);
        return;
      }

      toast.warning(`Unexpected: ${result.status}`);
    } catch (err) {
      const data = err.response?.data;
      const status = err.response?.status;
      if (data?.error === 'INVALID_GOOGLE_AUDIENCE') {
        toast.error(`Config error: aud=${data.receivedAud}, expected=${JSON.stringify(data.allowedAudiences)}`);
      } else {
        toast.error(data?.detail ? `Google auth failed (${status}): ${data.detail}` : 'Google sign-in failed.');
      }
    } finally {
      setGoogleLoading(false);
    }
  }, [googleAuth, linkGoogle, navigate, returnTo, user]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      redirectAfterAuth(navigate, returnTo);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(linkEmail, linkPassword);
      if (pendingGoogleToken) await linkGoogle(pendingGoogleToken);
      toast.success('Google linked!');
      setLinkRequired(false);
      redirectAfterAuth(navigate, '/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotStart = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const r = await forgotPasswordStart(forgotEmail);
      if (r.status === 'OTP_SENT') { setMode('forgot-otp'); toast.success('Code sent'); }
      else if (r.status === 'GOOGLE_ONLY_ACCOUNT') toast.info('Use Google to sign in.');
      else if (r.status === 'NOT_FOUND') toast.error('No account found');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const handleForgotVerify = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await forgotPasswordVerify(forgotEmail, otp, newPassword);
      toast.success('Password reset!'); setMode('login'); setEmail(forgotEmail);
    } catch (err) { toast.error(err.response?.data?.detail || 'Invalid code'); }
    finally { setLoading(false); }
  };

  // --- Google loading overlay ---
  if (googleLoading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-sm text-muted-foreground">Signing you in...</p>
        </div>
      </div>
    );
  }

  // --- LINK_REQUIRED ---
  if (linkRequired) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md p-8 border-border bg-card">
          <div className="text-center mb-4">
            <h1 className="font-display text-2xl text-foreground font-semibold mb-2">Link Your Accounts</h1>
            <p className="text-sm text-muted-foreground">Enter your password for <strong className="text-foreground">{linkEmail}</strong> to link Google.</p>
          </div>
          <form onSubmit={handleLinkLogin} className="space-y-4">
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={linkEmail} disabled className="opacity-70" /></div>
            <div className="space-y-2"><Label>Password</Label><PasswordInput value={linkPassword} onChange={(e) => setLinkPassword(e.target.value)} required autoFocus data-testid="link-password-input" placeholder="Enter your password" /></div>
            <Button type="submit" className="w-full" disabled={loading}>{loading ? 'Linking...' : 'Sign In & Link Google'}</Button>
          </form>
          <button className="w-full text-center text-xs text-muted-foreground mt-4 hover:text-foreground" onClick={() => { setLinkRequired(false); setPendingGoogleToken(''); }}>Cancel</button>
        </Card>
      </div>
    );
  }

  // --- Forgot OTP ---
  if (mode === 'forgot-otp') {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md p-8 border-border bg-card">
          <h1 className="font-display text-2xl text-foreground font-semibold mb-2">Reset Password</h1>
          <p className="text-sm text-muted-foreground mb-6">Code sent to <strong className="text-foreground">{forgotEmail}</strong></p>
          <form onSubmit={handleForgotVerify} className="space-y-4">
            <div className="space-y-2"><Label>Code</Label><Input type="text" inputMode="numeric" maxLength={6} placeholder="000000" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} required className="text-center text-2xl tracking-[0.5em] font-mono" /></div>
            <div className="space-y-2"><Label>New Password</Label><PasswordInput placeholder="Min 6 chars" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} data-testid="forgot-new-password-input" /></div>
            <Button type="submit" className="w-full" disabled={loading}>{loading ? 'Resetting...' : 'Reset Password'}</Button>
          </form>
          <Button variant="ghost" className="w-full mt-3 text-xs" onClick={() => setMode('login')}>Back</Button>
        </Card>
      </div>
    );
  }

  // --- Forgot start ---
  if (mode === 'forgot') {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md p-8 border-border bg-card">
          <h1 className="font-display text-2xl text-foreground font-semibold mb-2">Forgot Password</h1>
          <p className="text-sm text-muted-foreground mb-6">We'll send a verification code.</p>
          <form onSubmit={handleForgotStart} className="space-y-4">
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required /></div>
            <Button type="submit" className="w-full" disabled={loading}>{loading ? 'Sending...' : 'Send Code'}</Button>
          </form>
          <Button variant="ghost" className="w-full mt-3 text-xs" onClick={() => setMode('login')}>Back</Button>
        </Card>
      </div>
    );
  }

  // --- Main login ---
  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md p-8 border-border bg-card" data-testid="login-card">
        <div className="text-center mb-6">
          <h1 className="font-display text-3xl text-foreground font-semibold mb-2">Welcome Back</h1>
          <p className="text-muted-foreground text-sm">Sign in to your V watch account</p>
        </div>
        <form onSubmit={handleLogin} className="space-y-4 mt-6" data-testid="login-form">
          <div className="space-y-2"><Label htmlFor="email">Email</Label><Input id="email" type="email" placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" /></div>
          <div className="space-y-2">
            <div className="flex items-center justify-between"><Label htmlFor="password">Password</Label><button type="button" className="text-xs text-primary hover:underline" onClick={() => { setMode('forgot'); setForgotEmail(email); }}>Forgot password?</button></div>
            <PasswordInput id="password" placeholder="Enter password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">{loading ? 'Signing in...' : 'Sign In'}</Button>
        </form>
        <p className="text-sm text-muted-foreground text-center mt-6">Don't have an account? <Link to="/register" className="text-primary hover:underline">Sign up</Link></p>
      </Card>
    </div>
  );
}
